import { Component, inject, input, OnInit } from '@angular/core';
import { PropertyChargeFilter } from '../../../../core/filters/property-charge.filter';
import { ChargeStatus } from '../../../../core/enums/data.enums';
import { FaDuotoneIconComponent } from "@fortawesome/angular-fontawesome";
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiSelectComponent } from "../../../../parts/multi-select/multi-select.component";
import { NgxMaskDirective } from "ngx-mask";
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
  selector: 'ms-property-charge-search-filter',
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, ParentFocusDirective, MultiSelectComponent, NgxMaskDirective, DatePlaceholderDirective],
  templateUrl: './property-charge-search-filter.html',
  styleUrl: './property-charge-search-filter.scss'
})
export class PropertyChargeSearchFilter extends BaseSearchFilterComponent<PropertyChargeFilter> implements OnInit {
  public filter = input.required<PropertyChargeFilter>();
  public scheduleOptions = input<{ id: number, name: string }[]>([]);
  public preserveScheduleIdsOnReset = input<boolean>(true);
  public defaultFilterFactory = input<(() => PropertyChargeFilter) | undefined>(undefined);

  public selectChargeStatusOptions = [
    ChargeStatus.Open,
    ChargeStatus.Late,
    ChargeStatus.Paid,
    ChargeStatus.PaidLate,
    ChargeStatus.Locked,
    ChargeStatus.Voided,
    ChargeStatus.Pending
  ];

  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  // The minor units of the organization on screen, not the active one. This filter renders inside the
  // charge list on the member and property profiles, which are reachable for any organization the
  // viewer belongs to, and two organizations can use currencies with different minor units (USD 100
  // vs JPY 1) — converting with the wrong one shifts every amount by two decimal places (DEV-2643).
  private minorUnits(): number {
    return ProfileOrganizationScopeService.resolveMinorUnits(this.profileScope, this.userSessionService);
  }

  // Text inputs
  public invoiceNumberInput = '';
  public propertyTitleInput = '';
  public addressInput = '';
  public lotNumberInput = '';
  public selectedScheduleIdInput: number | '' = '';

  // Amount inputs (display values in major units/dollars)
  public minimumAmountOwedDisplay: number | undefined = undefined;
  public maximumAmountOwedDisplay: number | undefined = undefined;
  public minimumAmountPaidDisplay: number | undefined = undefined;
  public maximumAmountPaidDisplay: number | undefined = undefined;
  public minimumLateFeesDisplay: number | undefined = undefined;
  public maximumLateFeesDisplay: number | undefined = undefined;

  public selectedChargeStatus: ChargeStatus[] = [];

  constructor(private userSessionService: UserSessionService) {
    super();
  }

  public ngOnInit(): void {
    this.selectedChargeStatus = [...(this.filter().statuses || [])];
    this.initializeAmountDisplayValues();
    this.setFilterCount();
  }

  public override get activeFilter(): PropertyChargeFilter {
    return this.filter();
  }

  protected override getDefaultFilter(): PropertyChargeFilter {
    const factory = this.defaultFilterFactory();
    return factory ? factory() : ListConstants.PROPERTY_CHARGES_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof PropertyChargeFilter)[] {
    const keys: (keyof PropertyChargeFilter)[] = ['organizationIds', 'propertyIds'];
    if (this.preserveScheduleIdsOnReset()) {
      keys.push('propertyChargeScheduleIds');
    }
    return keys;
  }

  protected override get dropdownSelector(): string {
    return '#property-charge-search-filter-dropdown';
  }

  protected override beforeApply(): void {
    this.convertDisplayValuesToMinorUnits();
  }

  protected override syncStateFromFilter(): void {
    this.selectedChargeStatus = [...(this.activeFilter.statuses || [])];
    this.initializeAmountDisplayValues();
  }

  private initializeAmountDisplayValues(): void {
    const filter = this.filter();
    const minorUnits = this.minorUnits();
    this.minimumAmountOwedDisplay = filter.minimumAmountOwed != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.minimumAmountOwed, minorUnits) : undefined;
    this.maximumAmountOwedDisplay = filter.maximumAmountOwed != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.maximumAmountOwed, minorUnits) : undefined;
    this.minimumAmountPaidDisplay = filter.minimumAmountPaid != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.minimumAmountPaid, minorUnits) : undefined;
    this.maximumAmountPaidDisplay = filter.maximumAmountPaid != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.maximumAmountPaid, minorUnits) : undefined;
    this.minimumLateFeesDisplay = filter.minimumLateFees != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.minimumLateFees, minorUnits) : undefined;
    this.maximumLateFeesDisplay = filter.maximumLateFees != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.maximumLateFees, minorUnits) : undefined;
  }

  private convertDisplayValuesToMinorUnits(): void {
    const filter = this.filter();
    const minorUnits = this.minorUnits();
    filter.minimumAmountOwed = this.minimumAmountOwedDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minimumAmountOwedDisplay), minorUnits) : undefined;
    filter.maximumAmountOwed = this.maximumAmountOwedDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maximumAmountOwedDisplay), minorUnits) : undefined;
    filter.minimumAmountPaid = this.minimumAmountPaidDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minimumAmountPaidDisplay), minorUnits) : undefined;
    filter.maximumAmountPaid = this.maximumAmountPaidDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maximumAmountPaidDisplay), minorUnits) : undefined;
    filter.minimumLateFees = this.minimumLateFeesDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minimumLateFeesDisplay), minorUnits) : undefined;
    filter.maximumLateFees = this.maximumLateFeesDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maximumLateFeesDisplay), minorUnits) : undefined;
  }

  public override setFilterCount(): void {
    const filter = this.filter();
    this.filterCount =
      (filter.propertyChargeIds?.length || 0) +
      (filter.propertyChargeScheduleIds?.length || 0) +
      (filter.titles?.length || 0) +
      (filter.primaryAddresses?.length || 0) +
      (filter.lotNumbers?.length || 0) +
      (filter.statuses?.length || 0) +
      (filter.minimumIssuanceDate ? 1 : 0) +
      (filter.maximumIssuanceDate ? 1 : 0) +
      (filter.minimumDueDate ? 1 : 0) +
      (filter.maximumDueDate ? 1 : 0) +
      (filter.minimumAmountOwed ? 1 : 0) +
      (filter.maximumAmountOwed ? 1 : 0) +
      (filter.minimumAmountPaid ? 1 : 0) +
      (filter.maximumAmountPaid ? 1 : 0) +
      (filter.minimumLateFees != null ? 1 : 0) +
      (filter.maximumLateFees != null ? 1 : 0) +
      (filter.minimumPaidDate ? 1 : 0) +
      (filter.maximumPaidDate ? 1 : 0);
  }

  public addInvoiceNumber(): void {
    if (this.invoiceNumberInput && !(this.invoiceNumberInput.trim() === '')) {
      const invoiceId = parseInt(this.invoiceNumberInput, 10);
      if (!isNaN(invoiceId)) {
        this.filter().propertyChargeIds?.push(invoiceId);
      }
    }
    this.invoiceNumberInput = '';
  }

  public removeInvoiceNumber(index: number): void {
    this.filter().propertyChargeIds?.splice(index, 1);
  }

  public addPropertyTitle(): void {
    if (this.propertyTitleInput && !(this.propertyTitleInput.trim() === '')) {
      this.filter().titles?.push(this.propertyTitleInput);
    }
    this.propertyTitleInput = '';
  }

  public removePropertyTitle(index: number): void {
    this.filter().titles?.splice(index, 1);
  }

  public addAddress(): void {
    if (this.addressInput && !(this.addressInput.trim() === '')) {
      this.filter().primaryAddresses?.push(this.addressInput);
    }
    this.addressInput = '';
  }

  public removeAddress(index: number): void {
    this.filter().primaryAddresses?.splice(index, 1);
  }

  public addLotNumber(): void {
    if (this.lotNumberInput && !(this.lotNumberInput.trim() === '')) {
      this.filter().lotNumbers?.push(this.lotNumberInput);
    }
    this.lotNumberInput = '';
  }

  public removeLotNumber(index: number): void {
    this.filter().lotNumbers?.splice(index, 1);
  }

  public addSchedule(): void {
    if (this.selectedScheduleIdInput === '' || this.selectedScheduleIdInput === null || this.selectedScheduleIdInput === undefined) {
      return;
    }
    const id = Number(this.selectedScheduleIdInput);
    const filter = this.filter();
    if (!filter.propertyChargeScheduleIds) {
      filter.propertyChargeScheduleIds = [];
    }
    if (!filter.propertyChargeScheduleIds.includes(id)) {
      filter.propertyChargeScheduleIds.push(id);
    }
    this.selectedScheduleIdInput = '';
  }

  public removeSchedule(index: number): void {
    this.filter().propertyChargeScheduleIds?.splice(index, 1);
  }

  public getScheduleName(id: number): string {
    return this.scheduleOptions().find(opt => opt.id === id)?.name ?? `Schedule ${id}`;
  }

  public onStatusChange(event: any): void {
    this.filter().statuses = event;
  }
}
